import type { PortfolioMetricTypes, StandardClassificationId } from "@eu.mdotm.api/sphere";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { generateUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import type { ReportTemplateItem as ReportTemplateItemV7, ReportTemplate as ReportTemplateV7 } from "./report-v7";
import { typedObjectEntries } from "@mdotm/shadcn/legacy/utils";
import { typedObjectValues } from "$root/utils";

/** List used to show the buttons */
export const enabledTemplateItemKinds: Array<keyof ReportTemplateItemMap> = [
	"cover",
	"summary",
	"freeText",
	"portfolioMetrics",
	"exAntePortfolioMetrics",
	"commentary",
	"composition",
	"exposure",
	"performance",
	"performanceAttribution",
	"exAnteContributionVolatility",
	"performanceAndVolatilityContribution",
	"factorExposure",
	"disclaimer",
];

/** List used to disable buttons based on the report type */
export const availableTemplateItemKindsByReportType: Record<
	ReportTemplateVariant,
	Array<keyof ReportTemplateItemMap>
> = {
	current: [
		"cover",
		"summary",
		"freeText",
		"portfolioMetrics",
		"exAntePortfolioMetrics",
		"commentary",
		"composition",
		"exposure",
		"performance",
		"performanceAttribution",
		"exAnteContributionVolatility",
		"performanceAndVolatilityContribution",
		"factorExposure",
		"disclaimer",
	],
	proposal: [
		"cover",
		"summary",
		"freeText",
		"portfolioMetrics",
		"exAntePortfolioMetrics",
		"commentary",
		"composition",
		"exposure",
		"factorExposure",
		"disclaimer",
	],
};

type Forecast = "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS" | "ONE_YEAR" | "YEAR_TO_DATE" | "FROM_INCEPTION";
export type CompositionColumnPreference = {
	enabled: boolean;
	preferenceType: {
		standardClassificationId?: StandardClassificationId;
		classificationUuid: (string & {}) | "IDENTIFIER" | "WEIGHT" | "NAME" | "ENHANCED_WEIGHT" | "DIFFERENCE";
		label: string;
	};
};

export type ReportTemplateItemMap = {
	cover: {
		title?: string;
	};
	summary: Record<"portfolioName" | "baseCurrency" | "benchmark" | "universe" | "reference" | "averageScore", boolean>;
	freeText: {
		content: string;
	};
	portfolioMetrics: {
		preferences: Array<{
			metricType: PortfolioMetricTypes;
			metricParameter?: { classification?: { classificationUuid: string; option?: string } };
			enabled: boolean;
		}>;
		showBenchmark: boolean;
	};
	exAntePortfolioMetrics: {
		trackingError: Record<"TRACKING_ERROR_3Y", boolean>;
		return: Record<"RETURN_3Y", boolean>;
		maxDrawdown: Record<"MAX_DRAWDOWN_3Y", boolean>;
		volatility: Record<"VOLATILITY_3Y", boolean>; //"VOLATILITY_1Y" | "VOLATILITY_2Y" |
		efficiencyRatio: Record<"EFFICIENCY_RATIO_3Y", boolean>;
		diversificationRatio: Record<"DIVERSIFICATION_RATIO_3Y", boolean>;
		historicalVar: Record<"HISTORICAL_VAR_95_3Y", boolean>;
		riskConstraint: Record<"VAR" | "VOLATILITY" | "TRACKING_ERROR", boolean>;
		benchmark: boolean;
	};
	performance: {
		benchmark: boolean;
	};
	performanceAttribution: Record<Forecast, boolean>;
	commentary: {
		templateUuid?: string;
		title?: string;
		useTemplateNameAsTitle?: boolean;
		useTemplateLanguage?: boolean;
	};
	composition: {
		currentColumnPreferences: Array<CompositionColumnPreference>;
		proposalColumnPreferences: Array<CompositionColumnPreference>;
	};
	exposure: {
		selectedCategories: Array<
			| { value: string; label: string; type: "TAG" }
			| {
					value:
						| "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS"
						| "MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
						| "MACRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY"
						| "MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
						| "MICRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY"
						| "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY"
						| "CURRENCY"
						| "TAG";
					label: string;
					type: "RISK_MODEL";
			  }
		>;
	};
	exAnteContributionVolatility: {
		filterSignificant: boolean;
		versus: Record<
			| "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS"
			| "MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
			| "MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
			| "MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS"
			| "MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS",
			boolean
		>;
	};
	performanceAndVolatilityContribution: {
		filterSignificant: boolean;
		versus: Record<
			| "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS"
			| "MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
			| "MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY"
			| "MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS"
			| "MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS",
			boolean
		>;
	};
	factorExposure: {
		benchmark: boolean;
		filterSignificant: boolean;
	};
	disclaimer: {
		content: {
			enabled: boolean;
			title?: string;
			description?: string;
		};
		contact: {
			enabled: boolean;
			description?: string;
		};
	};
};

export const itemFactories = {
	cover: () => ({ title: undefined, kind: "cover", id: generateUniqueDOMId() }),
	summary: () => ({
		averageScore: false,
		baseCurrency: false,
		benchmark: false,
		portfolioName: false,
		reference: false,
		universe: false,
		kind: "summary",
		id: generateUniqueDOMId(),
	}),
	freeText: () => ({ content: "", kind: "freeText", id: generateUniqueDOMId() }),
	performance: () => ({ kind: "performance", id: generateUniqueDOMId(), benchmark: true }),
	portfolioMetrics: () => ({
		showBenchmark: false,
		preferences: [],
		kind: "portfolioMetrics",
		id: generateUniqueDOMId(),
	}),
	exAntePortfolioMetrics: () => ({
		benchmark: false,
		efficiencyRatio: {
			EFFICIENCY_RATIO_3Y: false,
		},
		maxDrawdown: {
			MAX_DRAWDOWN_3Y: false,
		},
		diversificationRatio: {
			DIVERSIFICATION_RATIO_3Y: false,
		},
		historicalVar: {
			HISTORICAL_VAR_95_3Y: false,
		},
		return: { RETURN_3Y: false },
		trackingError: { TRACKING_ERROR_3Y: false },
		volatility: {
			VOLATILITY_3Y: false,
		},
		riskConstraint: {
			TRACKING_ERROR: false,
			VAR: false,
			VOLATILITY: false,
		},
		kind: "exAntePortfolioMetrics",
		id: generateUniqueDOMId(),
	}),
	commentary: () => ({ detailed: true, summary: true, kind: "commentary", id: generateUniqueDOMId() }),
	composition: () => ({
		currentColumnPreferences: [],
		proposalColumnPreferences: [],
		kind: "composition",
		id: generateUniqueDOMId(),
	}),
	exposure: () => ({
		selectedCategories: [],
		kind: "exposure",
		id: generateUniqueDOMId(),
	}),
	exAnteContributionVolatility: () => ({
		versus: {
			MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
			MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
			MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: false,
			MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
			MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
		},
		filterSignificant: true,
		kind: "exAnteContributionVolatility",
		id: generateUniqueDOMId(),
	}),
	performanceAndVolatilityContribution: () => ({
		versus: {
			MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
			MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
			MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: false,
			MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
			MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
		},
		filterSignificant: true,
		kind: "performanceAndVolatilityContribution",
		id: generateUniqueDOMId(),
	}),
	performanceAttribution: () => ({
		FROM_INCEPTION: false,
		YEAR_TO_DATE: true,
		ONE_MONTH: false,
		THREE_MONTHS: false,
		SIX_MONTHS: false,
		ONE_YEAR: false,
		kind: "performanceAttribution",
		id: generateUniqueDOMId(),
	}),
	factorExposure: () => ({
		benchmark: false,
		filterSignificant: true,
		kind: "factorExposure",
		id: generateUniqueDOMId(),
	}),
	disclaimer: () => ({
		kind: "disclaimer",
		id: generateUniqueDOMId(),
		contact: { enabled: false, description: "" },
		content: { enabled: false, description: "" },
	}),
} satisfies { [K in keyof ReportTemplateItemMap]: (...params: Array<unknown>) => ReportTemplateItemFor<K> };

export const itemToIconMap: Record<keyof ReportTemplateItemMap, IconName> = {
	cover: "cover",
	freeText: "free-text",
	performance: "performance",
	summary: "summary",
	portfolioMetrics: "metrics",
	exAntePortfolioMetrics: "exante-metrics",
	commentary: "commentary-text",
	composition: "composition",
	exposure: "piechart",
	exAnteContributionVolatility: "Ex-antevolatilitycontribution",
	performanceAttribution: "Performance-attribution",
	performanceAndVolatilityContribution: "performance",
	factorExposure: "Factors-exposure",
	disclaimer: "disclaimer",
};

export const itemToConstraints: Record<
	keyof ReportTemplateItemMap,
	(ctx: { reportTemplate: ReportTemplate; templateVariantTab: ReportTemplateVariant }) => boolean
> = {
	cover: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "cover"),
	freeText: () => true,
	performanceAttribution: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "performanceAttribution",
		),
	summary: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "summary"),
	performance: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "performance"),
	portfolioMetrics: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "portfolioMetrics",
		),
	exAntePortfolioMetrics: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "exAntePortfolioMetrics",
		),
	commentary: () => true,
	composition: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "composition"),
	exposure: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "exposure"),
	exAnteContributionVolatility: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "exAnteContributionVolatility",
		),
	performanceAndVolatilityContribution: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "performanceAndVolatilityContribution",
		),
	factorExposure: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some(
			(item) => item.kind === "factorExposure",
		),
	disclaimer: (ctx) =>
		!ctx.reportTemplate.data.templateItemsByVariant[ctx.templateVariantTab].some((item) => item.kind === "disclaimer"),
};

export const itemToLabelMap: Record<keyof ReportTemplateItemMap, string> = {
	cover: "Cover page",
	freeText: "Text box",
	summary: "Summary",
	portfolioMetrics: "Portfolio metrics",
	exAntePortfolioMetrics: "Ex-ante metrics",
	commentary: "Commentary",
	composition: "Composition",
	exposure: "Exposure",
	exAnteContributionVolatility: "Ex-ante volatility contribution",
	performance: "Performance",
	performanceAndVolatilityContribution: "Performance and volatility contribution",
	performanceAttribution: "Performance attribution",
	factorExposure: "Factor exposure",
	disclaimer: "Closing page",
};

export const itemToAdderLabelMap: Record<keyof ReportTemplateItemMap, string> = {
	cover: "Cover\npage",
	freeText: "Text\nbox",
	summary: "Summary",
	portfolioMetrics: "Portfolio\nmetrics",
	exAntePortfolioMetrics: "Ex-ante\nmetrics",
	commentary: "Commentary",
	composition: "Composition",
	exposure: "Exposure",
	exAnteContributionVolatility: "Ex-ante vol.\ncontribution",
	performance: "Performance",
	performanceAttribution: "Perfomance\nattribution",
	performanceAndVolatilityContribution: "Performance &\nvolatility contrib.",
	factorExposure: "Factor\nexposure",
	disclaimer: "Closing\npage",
};

export type ReportTemplateItemFor<K extends keyof ReportTemplateItemMap> = {
	[InnerK in keyof ReportTemplateItemMap]: {
		kind: InnerK;
		id: string;
	} & ReportTemplateItemMap[InnerK];
}[K];

export type ReportTemplateItem = ReportTemplateItemFor<keyof ReportTemplateItemMap>;

export type ReportTemplateVariant = "current" | "proposal";
export type ReportTemplateStatus = "draft" | "ready";

export type ReportTemplate = {
	uuid: string;
	templateName: string;
	format: "pdf" | "xlsx";
	visible: boolean;
	data: {
		status: ReportTemplateStatus;
		version: number;
		templateItemsByVariant: Record<ReportTemplateVariant, Array<ReportTemplateItem>>;
		templateDraftItemsByVariant: Record<ReportTemplateVariant, Array<ReportTemplateItem>>;
	};
};

type PortfolioMetrics = Extract<ReportTemplateItemV7, { kind: "portfolioMetrics" }>;
function convertPortfolioMetrics(
	metricsPreference: Omit<PortfolioMetrics, "kind" | "id" | "benchmark">,
): ReportTemplateItemMap["portfolioMetrics"]["preferences"] {
	return typedObjectValues(metricsPreference).flatMap((preference) => {
		return typedObjectEntries(preference).map(
			([metricType, enabled]: [
				PortfolioMetricTypes,
				boolean,
			]): ReportTemplateItemMap["portfolioMetrics"]["preferences"][number] => ({
				enabled,
				metricType,
			}),
		);
	});
}

export function migrateV7ToV8(template: ReportTemplateV7): ReportTemplate {
	function migrateItem(item: ReportTemplateItemV7): ReportTemplateItem {
		switch (item.kind) {
			case "portfolioMetrics": {
				const { id, kind, benchmark, ...metricsPreferences } = item;

				return {
					id,
					kind,
					preferences: convertPortfolioMetrics(metricsPreferences),
					showBenchmark: benchmark,
				};
			}
			default:
				return item;
		}
		// migrate composition
	}

	return {
		...template,
		data: {
			version: 8,
			status: template.data.status,
			templateItemsByVariant: {
				current: template.data.templateItemsByVariant.current.map(migrateItem),
				proposal: template.data.templateItemsByVariant.proposal.map(migrateItem),
			},
			templateDraftItemsByVariant: {
				current: template.data.templateDraftItemsByVariant.current.map(migrateItem),
				proposal: template.data.templateDraftItemsByVariant.proposal.map(migrateItem),
			},
		},
	};
}
